import { IGroupedCountry } from "./ICountryList";
import i18n from "../i18n";
import DOMPurify from "dompurify";
import { TrackingData } from "shared/api/dtos/ITrackingDataDto";
import Cookies from "js-cookie";
import qs from "query-string";
// @ts-ignore
import * as Sentry from "@sentry/react";

// @ts-ignore
import { moment } from "cwb-react";

const shortid = require("shortid");
export const generateId = () => `item-${shortid.generate()}`;

export const stripBearerPrefix = (token: string): string => {
  if (!token) return token;
  return token.replace(/^Bearer\s+/i, '');
};

export const localeKeys: any = { en: 0, fr: 1, en_uk: 2, es: 3, nb: 99 };
export const listOfSpanishCountries = ["ES", "LATAM", "MX", "AR", "CL", "CO", "PE", "VE", "SV", "PY", "PA", "CR", "BO", "CU", "EC", "GT", "HT", "HN", "NI", "PR", "DO", "UY"];

export const errorPageRedirect = (errMessage?: string) => {
  console.trace('REDIRECTION', errMessage);
  if (process.env.NODE_ENV !== 'development') {
    window.location.replace(`${process.env.REACT_APP_CWB_500}`);
  }
}

export const isObjectEmpty = (obj:any) : boolean =>
  !obj || (Object.keys(obj).length === 0 && obj.constructor === Object);


export function useLocalStorage(key: string) {
  const data: any = JSON.parse(localStorage.getItem(key)) || {};

  return {
    get() {
      return data;
    },

    set(data: any) {
      localStorage.setItem(key, JSON.stringify(data));
      return data;
    },

    remove() {
      const deletedData = JSON.parse(localStorage.getItem(key));
      localStorage.removeItem(key);
      return deletedData;
    }
  };
}

const ONE_DAY = 24 * 60 * 60 * 1000;
export const diffDateDays = (date1: any, date2: any): number => {
  const date1Parsed: any = date1 instanceof Date ? date1 : new Date(date1);
  const date2Parsed: any = date2 instanceof Date ? date2 : new Date(date2);

  return Math.floor((date2Parsed - date1Parsed) / ONE_DAY);
};

export const timedSentrySendThenErrorPage = (e: any) => {
  Sentry.captureException(e);
  setTimeout(() => errorPageRedirect(), 1000);
};

export const getUserLanguageChoice = (
  localeKeys: any,
  languageChoice: number
): string => {
  return Object.keys(localeKeys).find((key: string) => {
    return localeKeys[key] === languageChoice;
  });
};

export const detectBrowserLang =
  (navigator.languages && navigator.languages[0]) || navigator.language;

export const getBrowserLang = () => {
  const browserLang = detectBrowserLang.toLowerCase();
  if (browserLang === "en-gb") {
    return "en_uk";
  } else if (localeKeys[browserLang.split("-")[0]]) {
    return browserLang.split("-")[0];
  } else {
    return "en";
  }
};

// Reads ?lang= from the URL and maps it to a supported locale key
// (en, fr, es, nb, en_uk — accepts tags like es-MX, en-GB, en_uk).
// Returns null when absent or unsupported.
export const getQueryStringLang = (): string | null => {
  const lang = new URLSearchParams(window.location.search).get("lang");
  if (!lang) return null;

  const lower = lang.toLowerCase().replace("_", "-");
  if (lower === "en-gb" || lower === "en-uk") return "en_uk";

  const base = lower.split("-")[0];
  return base in localeKeys ? base : null;
};

// Maps our internal locale keys to moment's locale codes. Only "en_uk" differs:
// moment's British English locale is "en-gb". "en_uk"/"en-uk" don't exist in
// moment, so passing them silently leaves the previous locale in place.
export const toMomentLocale = (lang: string): string =>
  lang === "en_uk" ? "en-gb" : lang;

export const trySetSpanishBrowserLang = () => {
  // An explicit ?lang= always wins, even over the dev country override.
  const qsLang = getQueryStringLang();
  if (qsLang) {
    i18n.changeLanguage(qsLang);
    moment.moment.locale(toMomentLocale(qsLang));
    return;
  }

  const languages = [];

  if (navigator.languages && navigator.languages) {
    for (let i = 0; i < navigator.languages.length; i++) {
      const element = navigator.languages[i];
      languages.push(element);
      if (i == 1)
        break;
    }
  } else {
    languages.push(navigator.language)
  }

  const esLanguage = languages.find(a => a.toLocaleLowerCase().startsWith("es"))
  if (esLanguage) {
    // Normalize regional tags (es-MX, es-419, ...) to "es" — the only Spanish
    // resource bundle we ship, and what `getI18n().language === "es"` checks expect.
    i18n.changeLanguage("es");
    moment.moment.locale("es");
  }
}

// Precedence: explicit `lang` argument (page-level force) > ?lang= querystring
// > browser preference.
export const setBrowserLang = (lang?: string) => {
  if (lang) {
    i18n.changeLanguage(lang);
    moment.moment.locale(toMomentLocale(lang));
    return;
  }

  const qsLang = getQueryStringLang();
  if (qsLang) {
    i18n.changeLanguage(qsLang);
    moment.moment.locale(toMomentLocale(qsLang));
    return;
  }

  const browserLang = detectBrowserLang.toLowerCase();
  if (browserLang === "en-gb") {
    i18n.changeLanguage("en_uk");
    moment.moment.locale("en-gb");
  } else if (localeKeys[browserLang.split("-")[0]]) {
    const normalizedBrowserLang = browserLang.split("-")[0];
    i18n.changeLanguage(normalizedBrowserLang);
    moment.moment.locale(normalizedBrowserLang);
  } else {
    i18n.changeLanguage("en");
    moment.moment.locale("en");
  }
};

export const setCurUserSignUp = (user: any) => {
  if (user && user.sessionId) {
    // This saved CWB_USER info is used by all API calls and in the user.api.js to cache responses
    localStorage.setItem(
      "CWB_SIGNUP_USER",
      JSON.stringify({ cachedAt: new Date().valueOf(), ...user })
    );
    sessionStorage.setItem("Session_Id", user.sessionId);
  } else {
    localStorage.removeItem("CWB_SIGNUP_USER");
    sessionStorage.removeItem("Session_Id");
  }
};

export const setUserActorSignUpId = (id: string) => {
  if (id == null) {
    localStorage.removeItem("Actor_SignUp_Id");
    return;
  }
  localStorage.setItem("Actor_SignUp_Id", id);
};

// Defined in ./countryStorage so dependency-free modules (the submission flow's
// geoCache) can share the same localStorage variable without importing this
// file, which boots i18n. Re-exported here so every existing
// `import { getCountryCode } from "helpers"` keeps working.
export {
  getCountryCode,
  getRegionCode,
  setCountryCode,
  setRegionCode,
} from "./countryStorage";

export const getUserActorSignUpId = () => {
  let userSignUpId = JSON.parse(localStorage.getItem("Actor_SignUp_Id"));
  if (!userSignUpId) return 0;
  return userSignUpId;
};

export const getCurUserSignUp = () => {
  let curUser = JSON.parse(localStorage.getItem("CWB_SIGNUP_USER"));
  if (!curUser || !curUser.sessionId) {
    return 0;
  }

  return curUser;
};

export const getSessionId = () => {
  let sessionId = sessionStorage.getItem("Session_Id");
  if (!sessionId) {
    return "";
  }

  return sessionId;
};

export const getWebSessionKey = () => {
  let webSessionKey = localStorage.getItem("CWB_WebSessionKey");
  if (!webSessionKey) {
    return "";
  }

  // cwb-react 3 saves the key as {"value","expiration"} (its setUserAuthToken, and its own reader
  // rewrites a bare key that way); this app saves it bare (setWebSessionKey below). Read both.
  if (webSessionKey.charAt(0) === "{") {
    try {
      const item = JSON.parse(webSessionKey);
      if (!item || !item.value || Date.now() > Number(item.expiration)) {
        localStorage.removeItem("CWB_WebSessionKey");
        return "";
      }
      return String(item.value);
    } catch (e) {
      // Not JSON after all: a bare key.
    }
  }

  const timeStamp = localStorage.getItem("CWB_Timestamp")
  if (!timeStamp) {
    return webSessionKey
  }

  const t1 = Number(timeStamp)
  const t2 = new Date().getTime();;
  if (Number.isFinite(t1) && !Number.isNaN(t1)) {
    var hours = Math.abs(t2 - t1) / 36e5;
    if (hours > 16) {
      localStorage.setItem("CWB_WebSessionKey", "");
      localStorage.removeItem("CWB_Timestamp");
      return "";
    }
  }
  return webSessionKey;
};

export const setWebSessionKey = (k: string) => {
  if (k) {
    localStorage.setItem("CWB_WebSessionKey", k);
    localStorage.setItem("CWB_Timestamp", new Date().getTime().toString())
  }
};

export const setCustomCouponCode = (id?: number, customCouponCode?: string) => {
  if (!customCouponCode)
    return;

  let dict: any = null;
  const jsonDict = sessionStorage.getItem("CWB_CustomCode");
  if (jsonDict) {
    dict = JSON.parse(jsonDict);
  }
  if (!dict) {
    dict = {};
  }
  const key = (id || -1).toString();
  dict[key] = customCouponCode;
  sessionStorage.setItem("CWB_CustomCode", JSON.stringify(dict))
}

let tempProductInfoStore: any = {};
export const setCustomProductInfo = (id?: number, productCode?: string, productServiceId?: number) => {
  if (!productCode)
    return;

  let dict: any = null;
  const jsonDict = sessionStorage.getItem("CWB_CustomProduct");
  if (jsonDict) {
    dict = JSON.parse(jsonDict);
  }
  if (!dict) {
    dict = {};
  }
  const key = (id || -1).toString();
  dict[key] = { productCode: productCode, serviceId: productServiceId };
  // sessionStorage.setItem("CWB_CustomProduct", JSON.stringify(dict))
  tempProductInfoStore = dict;
}

export const getCustomCouponCode = (id: number) => {
  const key = (id || -1).toString();

  const jsonDict = sessionStorage.getItem("CWB_CustomCode");
  let dict: any = null;
  if (jsonDict) {
    dict = JSON.parse(jsonDict);
  }

  if (!dict)
    return '';

  return (dict[key] || '');
}

export const deleteCustomProductInfo = (id: number) => {
  const key = (id || -1).toString();

  // const jsonDict = sessionStorage.getItem("CWB_CustomProduct");
  // let dict: any = null;
  // if (jsonDict) {
  //   dict = JSON.parse(jsonDict);
  // }
  let dict = tempProductInfoStore;

  dict[key] = null;
  delete dict[key];
  // sessionStorage.setItem("CWB_CustomProduct", JSON.stringify(dict))
}

export const getCustomProductInfo = (id: number) => {
  const key = (id || -1).toString();

  // const jsonDict = sessionStorage.getItem("CWB_CustomProduct");
  // let dict: any = null;
  // if (jsonDict) {
  //   dict = JSON.parse(jsonDict);
  // }

  let dict = tempProductInfoStore;
  if (!dict)
    return '';

  return (dict[key] || {});
}

// TODO: move this to a more narrowed helper like apiHelper...
export const postData = (url: any, data: any) => {
  // Default options are marked with *
  return fetch(url, {
    method: "POST", // *GET, POST, PUT, DELETE, etc.
    mode: "cors", // no-cors, cors, *same-origin
    credentials: "include", // include, *same-origin, omit
    cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    headers: {
      "Content-Type": "application/json",
      CWB: "Test",
    },
    // redirect: "follow", // manual, *follow, error
    // referrer: "no-referrer", // no-referrer, *client
    body: JSON.stringify(data), // body data type must match "Content-Type" header
  }); // parses JSON response into native JavaScript objects
};

// TODO: review and return Dict and create inteface for Select option items
export const groupCountryList = (countryWithCities: any): IGroupedCountry[] => {
  const counties = countryWithCities.reduce(function (r: any, country: any) {
    const category = country.countryId;
    !r[category]
      ? (r[category] = {
        label: country.countryName,
        value: country.countryCode,
        id: category,
        country,
        cityList: [country],
      })
      : r[category]["cityList"].push(country);
    return r;
  }, {}),
    result = Object.keys(counties).map(function (k) {
      return counties[k];
    });
  return result;
};

export const VerifyPhoneCodeLength = 4;

export const NameMaxLength = 55;

export const PasswordLength = 6;

export const buildSearchStyle = () => ({
  control: (base: any, state: any) => ({
    minHeight: "40px !important",
    width: "68px",
    border: "1px solid #D3DDE9 !important",
    borderRadius: "4px 0 0 4px",
    display: "flex",
  }),
  singleValue: (base: any) => ({
    ...base,
    marginLeft: "0",
    marginRight: "0",
    maxWidth: "100%",
    color: "#122640",
    fontSize: "12px",
    fontWeight: "600",
    width: "80%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    top: "55%",
  }),
  dropdownIndicator: () => ({
    paddingRight: "8px",
  }),
  indicatorSeparator: () => ({
    display: "none",
  }),
  container: (base: any) => ({
    ...base,
    width: "68px",
  }),
  placeholder: (base: any) => ({
    ...base,
    whiteSpace: "pre",
    textOverflow: "ellipsis",
  }),
  group: (base: any) => ({
    ...base,
    paddingTop: 2,
    overflowX: "hidden",
  }),
  groupHeading: (base: any) => ({
    ...base,
    textTransform: "titlecase",
    overflowX: "hidden",
  }),
  menu: (base: any) => ({
    ...base,
    borderRadius: "4px",
    border: "1px solid #D3DDE9",
    boxShadow: "0 2px 5px 0 rgba(0,0,0,0.05)",
    overflowX: "hidden",
    width: "180px",
    marginTop: "2px",
  }),
  menuList: (base: any) => ({
    ...base,
    overflowX: "hidden",
    maxHeight: "391px",
    paddingBottom: "6px",
    paddingTop: "6px",
  }),
  option: (base: any, state: any) => {
    return {
      ...base,
      backgroundColor: "#FFFFFF",
      padding: "6px 16px",
      color: "#495A6E",
      fontSize: 12,
      fontWeight: "normal",
      width: "100%",
    };
  },
});

export const getE164number = (
  phoneNumber: string,
  internationalCallingCode: string
): string => {
  let e164 = `+${internationalCallingCode}${phoneNumber}`;

  if (!e164.match(/^\+{1}[1-9]\d{1,15}$/i)) return "";

  return e164;
};

// A/B assignments must survive a cache clean, or a returning visitor is re-rolled
// into the other arm and the experiment measures the clean instead of the change.
const AB_TEST_KEYS = [
  'ab-test-home',
  'ab-test-home-date',
  'ab-test-submission-step4',
  'ab-test-submission-step4-date',
];

export const cleanCache = () => {
  const preserved: { [key: string]: string } = {};
  AB_TEST_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);
    // Only real values are carried over. Writing back a null stores the STRING
    // "null", which then reads as a valid-looking assignment nobody was given.
    if (value !== null) preserved[key] = value;
  });

  localStorage.clear();

  Object.keys(preserved).forEach((key) => localStorage.setItem(key, preserved[key]));
};

export const cleanObjectProperties = (obj: any) => {
  for (const property in obj)
    if (typeof obj[property] === "string" || obj[property] instanceof String)
      obj[property] = DOMPurify.sanitize(obj[property]);
};

// Query-string params parsed into named TrackingData fields.
// Everything here is best-effort: tracking must never break the app.
const MARKETING_PARAM_MAP: { [param: string]: string } = {
  utm_source: "utmSource",
  utm_medium: "utmMedium",
  utm_campaign: "utmCampaign",
  utm_term: "utmTerm",
  utm_content: "utmContent",
  gclid: "gclid",
  fbclid: "fbclid",
  msclkid: "msclkid",
};

const MERGEABLE_TRACKING_FIELDS = [
  "utmSource", "utmMedium", "utmCampaign", "utmTerm", "utmContent",
  "gclid", "fbclid", "msclkid",
  // set only via setInternalSource(), carried through merges like the others
  "internalSource", "internalSourceAt",
];

// Internal touch trail: breadcrumb of the last N surfaces, expiring after 1 day.
const INTERNAL_SOURCE_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const INTERNAL_SOURCE_MAX_ENTRIES = 3;
const INTERNAL_SOURCE_SEPARATOR = ">";

const isInternalSourceExpired = (data: TrackingData): boolean => {
  try {
    if (!data || !data.internalSource) return true;
    if (!data.internalSourceAt) return true;
    const lastTouch = Date.parse(data.internalSourceAt);
    if (isNaN(lastTouch)) return true;
    return Date.now() - lastTouch > INTERNAL_SOURCE_MAX_AGE_MS;
  } catch (e) {
    return true;
  }
};

// Links can inject a touchpoint via ?intSource=<label> (campaign deep-links,
// banners outside the SPAs). URL values are untrusted: the separator is
// stripped so one param can't fake multiple entries, and the length is capped.
const INTERNAL_SOURCE_URL_PARAM = "intSource";
const INTERNAL_SOURCE_MAX_ENTRY_LENGTH = 64;

const parseInternalSourceParam = (search: string): string | null => {
  try {
    if (!search) return null;
    const raw = new URLSearchParams(search).get(INTERNAL_SOURCE_URL_PARAM);
    if (!raw) return null;
    const cleaned = raw
      .split(INTERNAL_SOURCE_SEPARATOR).join("")
      .trim()
      .slice(0, INTERNAL_SOURCE_MAX_ENTRY_LENGTH);
    return cleaned || null;
  } catch (e) {
    return null;
  }
};

// First-touch context: once captured it is never overwritten.
const WRITE_ONCE_TRACKING_FIELDS = ["referrer", "landingPage"];

// Tracking attribution must not outlive 7 days.
const TRACKING_COOKIE_EXPIRY_DAYS = 7;

// The tData cookie is shared across CWB apps (signup, membership, classic site)
// when running on a known parent domain; anywhere else (localhost, ngrok, tests)
// it silently falls back to a host-only cookie.
const SHARED_COOKIE_PARENT_DOMAINS = [
  "castingworkbook.com",
  "thecwbint.com",
  "thetwbint.com",
  "thecwb.com",
  "thetwb.com",
  "theworkbook.com",
];

const getTrackingCookieDomain = (): string | undefined => {
  try {
    const hostname = (window.location.hostname || "").toLowerCase();
    for (let i = 0; i < SHARED_COOKIE_PARENT_DOMAINS.length; i++) {
      const parent = SHARED_COOKIE_PARENT_DOMAINS[i];
      if (hostname === parent || hostname.endsWith(`.${parent}`))
        return parent;
    }
  } catch (e) { /* tracking is best-effort */ }
  return undefined;
};

const parseMarketingParams = (search: string): Partial<TrackingData> => {
  const result = {} as any;
  if (!search) return result;
  try {
    const params = new URLSearchParams(search);
    Object.keys(MARKETING_PARAM_MAP).forEach((param) => {
      const value = params.get(param);
      if (value) result[MARKETING_PARAM_MAP[param]] = value;
    });
  } catch (e) { /* tracking is best-effort */ }
  return result as Partial<TrackingData>;
};

const readTrackingCookie = (): TrackingData | null => {
  try {
    const storedData = Cookies.get("tData");
    if (!storedData) return null;
    const parsed = JSON.parse(storedData) as TrackingData;

    // The internal touch trail must not outlive 1 day — enforced here so
    // every consumer (merge, transmit, rewrite) sees it consistently.
    if (parsed && parsed.internalSource && isInternalSourceExpired(parsed)) {
      delete (parsed as any).internalSource;
      delete (parsed as any).internalSourceAt;
    }

    return parsed;
  } catch (e) {
    // A corrupted cookie must never break signup/payment calls.
    return null;
  }
};

const writeTrackingCookie = (trackingData: TrackingData) => {
  try {
    const value = JSON.stringify(trackingData);
    const domain = getTrackingCookieDomain();
    if (domain) {
      // Remove any legacy host-only cookie so it can't shadow the shared one.
      try { Cookies.remove("tData", { path: "/" }); } catch (e) { /* best-effort */ }
      Cookies.set("tData", value, {
        expires: TRACKING_COOKIE_EXPIRY_DAYS,
        path: "/",
        domain,
        sameSite: "Lax",
        secure: window.location.protocol === "https:",
      });
      if (Cookies.get("tData")) return;
      // Browser rejected the domain-scoped cookie — fall back to host-only.
    }
    Cookies.set("tData", value, {
      expires: TRACKING_COOKIE_EXPIRY_DAYS,
      path: "/",
      sameSite: "Lax",
      secure: window.location.protocol === "https:",
    });
  } catch (e) { /* tracking is best-effort */ }
};

export const getTrackingData = (): TrackingData => {
  try {
    const existing = readTrackingCookie();
    if (!existing)
      return createFullPayloadTrackingData();

    const newTracking = createFullPayloadTrackingData();
    return mergeTrackingData(existing, newTracking);
  } catch (e) {
    return { queryParams: "", queryParamSource: "", browserLocale: i18n.language || "" } as TrackingData;
  }
};

const createFullPayloadTrackingData = (): TrackingData => {
  let current = window.location.search;

  const urlParams = new URLSearchParams(current);
  const myParam = urlParams.get('src')

  const trackingData = {
    queryParams: filterQueryStrings(current),
    queryParamSource: myParam,
    browserLocale: i18n.language
  } as TrackingData;

  Object.assign(trackingData, parseMarketingParams(current));

  return trackingData;
}

const mergeTrackingData = (existingSaved: TrackingData, newTracking: TrackingData): TrackingData => {
  let result = { queryParams: null, queryParamSource: null, browserLocale: newTracking.browserLocale || i18n.language } as TrackingData;

  if (newTracking.queryParamSource) {
    result.queryParamSource = newTracking.queryParamSource;
  } else {
    result.queryParamSource = existingSaved.queryParamSource;
  }

  // Marketing fields: the newest non-empty value wins, older values are kept.
  MERGEABLE_TRACKING_FIELDS.forEach((field) => {
    const value = (newTracking as any)[field] || (existingSaved as any)[field];
    if (value) (result as any)[field] = value;
  });

  // First-touch context is write-once: the existing value always wins.
  WRITE_ONCE_TRACKING_FIELDS.forEach((field) => {
    const value = (existingSaved as any)[field] || (newTracking as any)[field];
    if (value) (result as any)[field] = value;
  });

  if (!existingSaved.queryParams) {
    result.queryParams = newTracking.queryParams
    return result;
  }

  if (!newTracking.queryParams) {
    result.queryParams = existingSaved.queryParams;
    return result;
  }

  const resultParams = new URLSearchParams();

  const oldParams = new URLSearchParams(existingSaved.queryParams);
  const newParams = new URLSearchParams(newTracking.queryParams);

  const currentKeys = Array.from(newParams.keys());
  for (let i = 0; i < currentKeys.length; i++) {
    const element = currentKeys[i];
    resultParams.set(element, newParams.get(element));
  }

  const trackingKeys = Array.from(oldParams.keys())
  for (let i = 0; i < trackingKeys.length; i++) {
    const element = trackingKeys[i];
    if (resultParams.get(element))
      continue;

    resultParams.set(element, oldParams.get(element));
  }

  result.queryParams = '?' + resultParams.toString()
  return result;
}

const buildTrackingData = (
  srcQueryString: string,
  allOtherQueryStrings: string
): TrackingData => {
  let trackingData = {} as TrackingData;
  if (srcQueryString)
    trackingData.queryParamSource = Array.isArray(srcQueryString)
      ? srcQueryString[0]
      : srcQueryString;

  if (allOtherQueryStrings)
    trackingData.queryParams = filterQueryStrings(allOtherQueryStrings);

  trackingData.browserLocale = i18n.language;

  Object.assign(trackingData, parseMarketingParams(allOtherQueryStrings));

  return trackingData;
};

// Functional/sensitive params stripped before persisting to the tData cookie.
// Keep IDENTICAL to React-Membership's trackingData.js. `k`/`tk` are auth
// tokens and must never be stored in the parent-domain cookie; `eref` is a
// signed email reference (Signup/docs/34).
const QUERY_PARAM_BLACKLIST = [
  "join", "code", "promo", "page", "redirect", "homekit",
  "address", "redirectUrl", "product", "returnUrl",
  "k", "tk", "eref", "t",
];

const filterQueryStrings = (queryString: string) => {
  if (!queryString) return queryString;

  try {
    const params = new URLSearchParams(queryString);
    QUERY_PARAM_BLACKLIST.forEach((key) => params.delete(key));
    // '?'-prefixed like window.location.search / mergeTrackingData output —
    // keep the format IDENTICAL to React-Membership's trackingData.js.
    const filtered = params.toString();
    return filtered ? `?${filtered}` : "";
  } catch (e) {
    return "";
  }
};

const isNewTrackingDataEqualExisting = (
  srcQueryString: string,
  allOtherQueryStrings: string
) => {
  const existing = readTrackingCookie();
  if (!existing) return false;

  const candidate = buildTrackingData(srcQueryString, allOtherQueryStrings);

  // Apply the same backfill logic as setTrackingData() so write-once fields
  // (referrer, landingPage) and already-attributed mergeable fields don't
  // cause a spurious inequality → repeated cookie rewrites → sliding expiry.
  if (!candidate.queryParamSource && existing.queryParamSource)
    candidate.queryParamSource = existing.queryParamSource;
  MERGEABLE_TRACKING_FIELDS.forEach((field) => {
    if (!(candidate as any)[field] && (existing as any)[field])
      (candidate as any)[field] = (existing as any)[field];
  });
  candidate.referrer = existing.referrer;
  candidate.landingPage = existing.landingPage;

  return JSON.stringify(candidate) === JSON.stringify(existing);
};

const containData = (srcQueryString: string, allOtherQueryStrings: string) => {
  if (!srcQueryString && !allOtherQueryStrings) return false;

  return true;
};

const doesCacheNeedUpdate = (
  srcQueryString: string,
  allOtherQueryStrings: string
) => {
  const existingTrackingData = getTrackingData();
  if (
    existingTrackingData &&
    ((existingTrackingData.queryParams &&
      existingTrackingData.queryParams.length) ||
      (existingTrackingData.queryParamSource &&
        existingTrackingData.queryParamSource.length))
  ) {
    if (!containData(srcQueryString, allOtherQueryStrings)) return false;

    if (
      containData(srcQueryString, allOtherQueryStrings) &&
      isNewTrackingDataEqualExisting(srcQueryString, allOtherQueryStrings)
    )
      return false;
  }

  return true;
};

export const setTrackingData = (
  srcQueryString: string,
  allOtherQueryStrings: string
) => {
  try {
    // ?intSource=<label> appends to the breadcrumb trail — handled before the
    // cache-freshness early-return below so the touch is never skipped.
    const urlInternalSource = parseInternalSourceParam(allOtherQueryStrings);
    if (urlInternalSource) setInternalSource(urlInternalSource, true);

    if (!doesCacheNeedUpdate(srcQueryString, allOtherQueryStrings)) return;

    let trackingData = buildTrackingData(srcQueryString, allOtherQueryStrings);
    const existing = readTrackingCookie();

    // Keep previously captured attribution when the current URL doesn't carry it.
    if (existing) {
      if (!trackingData.queryParamSource && existing.queryParamSource)
        trackingData.queryParamSource = existing.queryParamSource;

      MERGEABLE_TRACKING_FIELDS.forEach((field) => {
        if (!(trackingData as any)[field] && (existing as any)[field])
          (trackingData as any)[field] = (existing as any)[field];
      });
    }

    // First-touch context, write-once: only captured when not already stored.
    const safeReferrer = (() => {
      try {
        if (!document.referrer) return undefined;
        const u = new URL(document.referrer);
        return `${u.origin}${u.pathname}`;
      } catch (e) {
        return undefined;
      }
    })();

    trackingData.referrer = (existing && existing.referrer) || safeReferrer;
    trackingData.landingPage =
      (existing && existing.landingPage) ||
      `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
    writeTrackingCookie(trackingData);
  } catch (e) {
    // Tracking capture must never break page rendering.
  }
};

/**
 * Record an internal touchpoint (page/paywall/dashboard) into the shared
 * tracking cookie as a breadcrumb trail of the last 3 movements, e.g.
 * "membership/offer-paywall>membership/promo-paywall>signup/checkout".
 * The trail expires 1 day after the last touch. Never throws.
 *
 * dedupeEntireTrail: pass true for URL-injected entries (?intSource=) — a
 * param that sticks in the URL across route mounts must never repeat in the
 * trail, only refresh the timestamp. App-code touchpoints keep the default
 * tail-only dedupe so legitimate revisits (paywall>checkout>paywall) remain.
 */
export const setInternalSource = (source: string, dedupeEntireTrail: boolean = false) => {
  try {
    if (!source) return;

    // readTrackingCookie has already dropped an expired trail; a fresh payload
    // comes with queryParams already blacklist-filtered.
    const existing = readTrackingCookie();
    const trackingData = existing || createFullPayloadTrackingData();

    const entries = trackingData.internalSource
      ? trackingData.internalSource.split(INTERNAL_SOURCE_SEPARATOR)
      : [];

    // Re-touching the same surface refreshes the timestamp, not the trail.
    const alreadyInTrail = dedupeEntireTrail
      ? entries.indexOf(source) !== -1
      : entries[entries.length - 1] === source;
    if (!alreadyInTrail) entries.push(source);

    trackingData.internalSource = entries
      .slice(-INTERNAL_SOURCE_MAX_ENTRIES)
      .join(INTERNAL_SOURCE_SEPARATOR);
    trackingData.internalSourceAt = new Date().toISOString();

    writeTrackingCookie(trackingData);
  } catch (e) {
    // Tracking capture must never break page rendering.
  }
};

// Return route location search query params.src
// Fallback is to use persisted tracking data
export const findTrackingData = (locationSearch: string): TrackingData => {
  try {
    if (!locationSearch) return getTrackingData();

    const params = qs.parse(locationSearch);
    if (!params) return getTrackingData();

    let trackingData = {} as TrackingData;
    if (params.src) trackingData.queryParamSource = params.src as string;

    trackingData.queryParams = filterQueryStrings(locationSearch);
    Object.assign(trackingData, parseMarketingParams(locationSearch));

    // Backfill anything the current URL doesn't carry from the persisted cookie.
    const stored = readTrackingCookie();
    if (stored) {
      if (!trackingData.queryParamSource && stored.queryParamSource)
        trackingData.queryParamSource = stored.queryParamSource;

      MERGEABLE_TRACKING_FIELDS.concat(WRITE_ONCE_TRACKING_FIELDS).forEach((field) => {
        if (!(trackingData as any)[field] && (stored as any)[field])
          (trackingData as any)[field] = (stored as any)[field];
      });
    }

    return trackingData;
  } catch (e) {
    return getTrackingData();
  }
};

// https://jsfiddle.net/nick_gaudreau_cwb/36sjz40n/24/

export const toFixed = (x: any) => {
  if (Math.abs(x) < 1.0) {
    let e = parseInt(x.toString().split("e-")[1]);
    if (e) {
      x *= Math.pow(10, e - 1);
      x = "0." + new Array(e).join("0") + x.toString().substring(2);
    }
  } else {
    let e = parseInt(x.toString().split("+")[1]);
    if (e > 20) {
      e -= 20;
      x /= Math.pow(10, e);
      x += new Array(e + 1).join("0");
    }
  }
  return x;
};

export const toFixedTrunc = (x: any, n: any) => {
  x = toFixed(x);

  // From here on the code is the same than the original answer
  const v = (typeof x === "string" ? x : x.toString()).split(".");
  if (n <= 0) return v[0];
  let f = v[1] || "";
  if (f.length > n) return `${v[0]}.${f.substr(0, n)}`;
  while (f.length < n) f += "0";
  return `${v[0]}.${f}`;
};

export const loadFont = (fontLink: string) => {
  var link = document.createElement("link");
  link.setAttribute("rel", "stylesheet");
  link.setAttribute("type", "text/css");
  link.setAttribute("href", fontLink);
  document.getElementsByTagName("head")[0].appendChild(link);
}
